import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { TableMap, cellAround, updateColumnsOnResize } from 'prosemirror-tables';

import { activeSystemZoom } from '../../../_util/table-resize-zoom.util';

// Column resizing by finger (iPad, phones). prosemirror-tables only resizes with a mouse: it finds the
// handle on mousemove (a touch screen has no hover) and drags on mousedown/mousemove, so on a tablet a
// column could not be resized at all. Here a horizontal drag that starts within a finger's width of a
// cell's edge resizes the column on that edge's left — live, without scrolling the page — and the new
// width is stored in the cells' colwidth like a mouse resize. A vertical drag scrolls as usual and a tap
// places the caret as usual. The visible grip on the first row is in styles.scss.

export const TABLE_TOUCH_EDGE_PX = 18;
const CELL_MIN_WIDTH = 25;

export const tableTouchResizeKey = new PluginKey('tableTouchResize');

interface TouchDrag {
  /** Position of the cell whose right edge is dragged. */
  cellPos: number;
  startX: number;
  startY: number;
  startWidth: number;
  /** null until the first move says whether it's a resize (horizontal) or a scroll (vertical). */
  resizing: boolean | null;
  width: number;
}

export function domCellAround(target: EventTarget | null): HTMLElement | null {
  let el = target instanceof Node ? (target as Node) : null;
  while (el && el.nodeName !== 'TD' && el.nodeName !== 'TH') {
    if (el instanceof HTMLElement && el.classList.contains('ProseMirror')) return null;
    el = el.parentNode;
  }
  return el as HTMLElement | null;
}

/** Position of the cell whose right edge is under the touch, or -1 when the touch isn't on an edge. */
export function touchedEdgeCell(view: EditorView, cellDom: HTMLElement, clientX: number, edgePx = TABLE_TOUCH_EDGE_PX): number {
  const rect = cellDom.getBoundingClientRect();
  const nearRight = rect.right - clientX <= edgePx;
  const nearLeft = clientX - rect.left <= edgePx;
  if (!nearRight && !nearLeft) return -1;

  let $cell;
  try {
    $cell = cellAround(view.state.doc.resolve(view.posAtDOM(cellDom, 0)));
  } catch {
    return -1;
  }
  if (!$cell) return -1;
  if (nearRight) return $cell.pos;

  // On a cell's left edge: resize the column before it (none for the first column).
  const map = TableMap.get($cell.node(-1));
  const start = $cell.start(-1);
  const index = map.map.indexOf($cell.pos - start);
  return index % map.width === 0 ? -1 : start + map.map[index - 1];
}

function currentColWidth(view: EditorView, cellPos: number, node: ProseMirrorNode): number {
  const { colspan, colwidth } = node.attrs as { colspan: number; colwidth: number[] | null };
  const width = colwidth && colwidth[colwidth.length - 1];
  if (width) return width;
  const dom = view.nodeDOM(cellPos) as HTMLElement | null;
  let domWidth = dom?.offsetWidth ?? 0;
  let parts = colspan;
  if (colwidth) {
    for (let i = 0; i < colspan; i++) {
      if (colwidth[i]) {
        domWidth -= colwidth[i];
        parts--;
      }
    }
  }
  return domWidth / Math.max(1, parts);
}

export function columnOf(view: EditorView, cellPos: number): { table: ProseMirrorNode; start: number; col: number } {
  const $cell = view.state.doc.resolve(cellPos);
  const table = $cell.node(-1);
  const start = $cell.start(-1);
  const col = TableMap.get(table).colCount($cell.pos - start) + ($cell.nodeAfter?.attrs['colspan'] ?? 1) - 1;
  return { table, start, col };
}

/** Shows the width while dragging, without touching the document. */
function displayColumnWidth(view: EditorView, cellPos: number, width: number): void {
  const { table, start, col } = columnOf(view, cellPos);
  let dom: Node | null = view.domAtPos(start).node;
  while (dom && dom.nodeName !== 'TABLE') dom = dom.parentNode;
  if (!dom) return;
  updateColumnsOnResize(table, (dom as HTMLTableElement).firstChild as HTMLTableColElement, dom as HTMLTableElement, CELL_MIN_WIDTH, col, width);
}

/** Stores the width in the colwidth of every cell of the column (as prosemirror-tables does on mouseup). */
export function setColumnWidth(view: EditorView, cellPos: number, width: number): void {
  const { table, start, col } = columnOf(view, cellPos);
  const map = TableMap.get(table);
  const tr = view.state.tr;
  for (let row = 0; row < map.height; row++) {
    const mapIndex = row * map.width + col;
    if (row && map.map[mapIndex] === map.map[mapIndex - map.width]) continue;
    const pos = map.map[mapIndex];
    const attrs = table.nodeAt(pos)!.attrs;
    const index = attrs['colspan'] === 1 ? 0 : col - map.colCount(pos);
    if (attrs['colwidth'] && attrs['colwidth'][index] === width) continue;
    const colwidth: number[] = attrs['colwidth'] ? attrs['colwidth'].slice() : Array(attrs['colspan']).fill(0);
    colwidth[index] = width;
    tr.setNodeMarkup(start + pos, null, { ...attrs, colwidth });
  }
  if (tr.docChanged) view.dispatch(tr);
}

export const TableTouchResize = Extension.create({
  name: 'tableTouchResize',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: tableTouchResizeKey,
        view: (view) => {
          let drag: TouchDrag | null = null;

          const end = (commit: boolean) => {
            const finished = drag;
            drag = null;
            view.dom.classList.remove('table-touch-resizing');
            if (!finished?.resizing) return;
            if (commit) setColumnWidth(view, finished.cellPos, finished.width);
            else displayColumnWidth(view, finished.cellPos, finished.startWidth);
          };

          const onTouchStart = (event: TouchEvent) => {
            drag = null;
            if (!view.editable || event.touches.length !== 1) return;
            const touch = event.touches[0];
            const cellDom = domCellAround(event.target);
            if (!cellDom) return;
            const cellPos = touchedEdgeCell(view, cellDom, touch.clientX);
            if (cellPos < 0) return;
            const node = view.state.doc.nodeAt(cellPos);
            if (!node) return;
            const startWidth = currentColWidth(view, cellPos, node);
            drag = { cellPos, startX: touch.clientX, startY: touch.clientY, startWidth, resizing: null, width: startWidth };
          };

          const onTouchMove = (event: TouchEvent) => {
            if (!drag) return;
            if (event.touches.length !== 1) {
              end(false);
              return;
            }
            const touch = event.touches[0];
            const dx = touch.clientX - drag.startX;
            if (drag.resizing === null) {
              // The first move decides — iOS only lets a touchmove stop scrolling if the first one does.
              const dy = touch.clientY - drag.startY;
              drag.resizing = Math.abs(dx) >= Math.abs(dy);
              if (!drag.resizing) {
                drag = null;
                return;
              }
              view.dom.classList.add('table-touch-resizing');
            }
            if (event.cancelable) event.preventDefault();
            drag.width = Math.max(CELL_MIN_WIDTH, Math.round(drag.startWidth + dx / activeSystemZoom()));
            displayColumnWidth(view, drag.cellPos, drag.width);
          };

          const onTouchEnd = () => end(true);
          const onTouchCancel = () => end(false);

          view.dom.addEventListener('touchstart', onTouchStart, { passive: true });
          view.dom.addEventListener('touchmove', onTouchMove, { passive: false });
          view.dom.addEventListener('touchend', onTouchEnd);
          view.dom.addEventListener('touchcancel', onTouchCancel);

          return {
            destroy: () => {
              view.dom.removeEventListener('touchstart', onTouchStart);
              view.dom.removeEventListener('touchmove', onTouchMove);
              view.dom.removeEventListener('touchend', onTouchEnd);
              view.dom.removeEventListener('touchcancel', onTouchCancel);
            },
          };
        },
      }),
    ];
  },
});
